import { useState, useEffect, useRef } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { RoleProvider } from "./contexts/RoleContext";
import { supabase, redirectToLogin, redirectToHubChangePassword, shouldForcePasswordChange } from "@/lib/supabase";
import Index from "./pages/Index";
import LandingPage from "./pages/LandingPage";
import NotFound from "./pages/NotFound";
import { PropertyDetailsPage as AdminPropertyDetailsPage } from "./pages/PropertyDetailsPage";
import { WorkerDetailsPage } from "./pages/WorkerDetailsPage";
import AdminDashboard from "./pages/AdminDashboard";
import EquipmentProtocolHistoryPage from "./pages/EquipmentProtocolHistoryPage";
import { ThemeSync } from "@/components/ThemeSync";
import { LanguageSync } from "@/components/LanguageSync";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { CleanerDashboard } from "@/components/cleaner/CleanerDashboard";
import { CleanerRuntimeGuards } from "@/components/cleaner/CleanerRuntimeGuards";
import { SectionDetailsPage } from "./pages/cleaner/SectionDetailsPage";
import { PropertyDetailsPage as CleanerPropertyDetailsPage } from "./pages/cleaner/PropertyDetailsPage";
import { RoleSelectionPage } from "@/components/auth/RoleSelectionPage";
import { AdminLayout } from "@/components/layouts/AdminLayout";
import { toast } from "sonner";
import NoModuleAccess from "@/pages/NoModuleAccess";
import { isCleaningUiRole, userHasModuleAccess } from "@/lib/moduleAccess";

const ACTIVE_ROLE_STORAGE_KEY = "domio-active-role";
const ACTIVE_ORG_STORAGE_KEY = "domio-active-org-id";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: true,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      staleTime: 1000 * 60 * 5,
    },
  },
});

type UserRole = 'owner' | 'coordinator' | 'cleaner' | null;

interface UserRoleData {
  role: UserRole;
  orgId: string | null;
}

interface MembershipData {
  role: string;
  org_id: string | null;
}

function AppInner() {
  const [session, setSession] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userRole, setUserRole] = useState<UserRole>(null);
  const [userOrgId, setUserOrgId] = useState<string | null>(null);
  const [isRoleLoading, setIsRoleLoading] = useState(false);
  const [needsRoleSelection, setNeedsRoleSelection] = useState(false);
  const [availableMemberships, setAvailableMemberships] = useState<MembershipData[]>([]);
  const [moduleAccess, setModuleAccess] = useState<"pending" | "allowed" | "denied">("pending");

  // Globalny Kill-Switch dla CSS Lock - siłowe usuwanie blokad Radix UI przy każdym powrocie do aplikacji
  useEffect(() => {
    const unlock = () => {
      document.body.style.pointerEvents = 'auto';
      document.body.style.overflow = 'auto';
      // Usuń data attributes z Radix UI
      document.body.removeAttribute('data-radix-scroll-lock-scrollbar-size');
      document.body.removeAttribute('data-radix-scroll-lock-content');
    };

    // Odblokuj przy focus i visibilitychange
    window.addEventListener('focus', unlock);
    document.addEventListener('visibilitychange', unlock);

    // Także odblokuj natychmiast
    unlock();

    return () => {
      window.removeEventListener('focus', unlock);
      document.removeEventListener('visibilitychange', unlock);
    };
  }, []);
  // CRITICAL: Track if UniversalSessionBridge has completed initialization
  // This prevents showing auth errors before bridge finishes its work
  const [isBridgeInitialized, setIsBridgeInitialized] = useState(false);
  // CRITICAL: Track if application is fully initialized
  // This separates first load from background refresh (focus events)
  // isInitialized becomes true after first initialization completes
  const [isInitialized, setIsInitialized] = useState(false);
  // CRITICAL: Track if role fetch has been attempted
  // This prevents showing auth error during role fetching, only after it fails
  const [roleFetchAttempted, setRoleFetchAttempted] = useState(false);
  
  // CRITICAL: Refs to prevent stale closure in onAuthStateChange
  // These refs always hold the current value, avoiding stale closure issues
  const isInitializedRef = useRef(false);
  const isLoadingRef = useRef(true);
  
  // Diagnostic overlay state
  const [diagnostics, setDiagnostics] = useState({
    sessionStatus: 'Checking...',
    roleStatus: 'N/A',
    orgIdStatus: 'N/A',
    tenantIdStatus: 'N/A',
    loadingStatus: true,
    errorStatus: null as string | null,
    authTokenStatus: 'Checking...',
    cookieStatus: 'Checking...',
  });

  // Fetch user role and org_id from memberships table
  // This ensures we have both role and org_id for proper RBAC and data isolation
  // IMPORTANT: Verify RLS (Row Level Security) policy in Supabase for memberships table
  // User must have SELECT permission for their own user_id: auth.uid() = user_id
  const fetchUserRole = async (userId: string): Promise<UserRoleData> => {
    try {
      
      
      
      const { data: membershipData, error: membershipError } = await supabase
        .from('memberships')
        .select('role, org_id')
        .eq('user_id', userId)
        .eq('is_active', true);

      // DEBUG: Logowanie błędów
      if (membershipError) {
        console.error('Błąd pobierania ról:', membershipError);
        const errorMessage = `Błąd zapytania memberships: ${membershipError.message}`;
        console.error('[App] Błąd pobierania memberships:', membershipError);
        setDiagnostics(prev => ({
          ...prev,
          errorStatus: errorMessage,
          roleStatus: 'ERROR',
          orgIdStatus: 'ERROR',
          tenantIdStatus: 'ERROR',
        }));
        // CRITICAL: Mark role fetch as attempted and stop loading (error case)
        setRoleFetchAttempted(true);
        setIsRoleLoading(false);
        return { role: null, orgId: null };
      }

      // DEBUG: Logowanie pobranych członkostw
      
      

      if (!membershipData || !Array.isArray(membershipData) || membershipData.length === 0) {
        const errorMessage = 'Brak danych w tabeli memberships dla tego użytkownika';
        console.error('[App] Błąd: Brak członkostw dla użytkownika');
        setDiagnostics(prev => ({
          ...prev,
          errorStatus: errorMessage,
          roleStatus: 'BRAK DANYCH',
          orgIdStatus: 'BRAK DANYCH',
          tenantIdStatus: 'BRAK DANYCH',
        }));
        // CRITICAL: Mark role fetch as attempted and stop loading (error case)
        setRoleFetchAttempted(true);
        setIsRoleLoading(false);
        return { role: null, orgId: null };
      }

      // Type assertion needed due to Supabase complex typing
      const typedData = membershipData as MembershipData[];
      const uiMemberships = typedData.filter((m) => isCleaningUiRole(m.role));

      // Store available memberships for role selection
      setAvailableMemberships(uiMemberships);

      // UODPORNIENIE: Check if user has multiple roles (case-insensitive comparison)
      // Normalize roles to lowercase for comparison, but keep original values
      // Always operate on lowercase to prevent comparison errors
      const uniqueRoles = [...new Set(uiMemberships.map(m => m.role.toLowerCase().trim()))];
      
      
      
      // If no unique roles found, handle error case
      if (uniqueRoles.length === 0) {
        console.error('[App] BŁĄD: Brak unikalnych ról po normalizacji');
        setNeedsRoleSelection(false);
        // Show error toast
        toast.error('Brak przypisanych ról w bazie danych!');
        setDiagnostics(prev => ({
          ...prev,
          errorStatus: 'Brak przypisanych ról w bazie danych!',
          roleStatus: 'BRAK RÓL',
        }));
        // CRITICAL: Mark role fetch as attempted before returning
        setRoleFetchAttempted(true);
        setIsRoleLoading(false);
        return { role: null, orgId: null };
      }

      const savedRoleSelection = localStorage.getItem(ACTIVE_ROLE_STORAGE_KEY);
      const savedOrgId = localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);

      let selectedRole: UserRole = null;
      let selectedOrgId: string | null = null;

      // If user has only one role, auto-select it
      if (uniqueRoles.length === 1) {
        const singleMembership = uiMemberships[0];
        // Normalize role to lowercase for comparison, but use original value
        const normalizedRole = singleMembership.role.toLowerCase();
        // Map normalized role back to expected UserRole type
        if (normalizedRole === 'owner' || normalizedRole === 'coordinator' || normalizedRole === 'cleaner') {
          selectedRole = normalizedRole as UserRole;
        } else {
          // Fallback: use original role if normalization doesn't match
          selectedRole = singleMembership.role as UserRole;
        }
        selectedOrgId = singleMembership.org_id;
        setNeedsRoleSelection(false);
        if (selectedRole) {
          localStorage.setItem(ACTIVE_ROLE_STORAGE_KEY, selectedRole);
        }
        if (selectedOrgId) {
          localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, selectedOrgId);
        }
        
      } 
      // If user has multiple roles, use active role persisted by explicit user choice
      else if (uniqueRoles.length > 1) {
        const isRootPath = window.location.pathname === '/';

        // On root path always force explicit role selection screen.
        // Saved activeRole is ignored intentionally for "/" entry.
        if (isRootPath) {
          setRoleFetchAttempted(true);
          setIsRoleLoading(false);
          setNeedsRoleSelection(true);
          return { role: null, orgId: null };
        }

        const savedMembership = savedRoleSelection && savedOrgId
          ? uiMemberships.find(
              (m) =>
                m.role.toLowerCase().trim() === savedRoleSelection.toLowerCase().trim() &&
                m.org_id === savedOrgId
            )
          : null;

        if (savedMembership) {
          selectedRole = savedMembership.role.toLowerCase().trim() as UserRole;
          selectedOrgId = savedMembership.org_id;
          setNeedsRoleSelection(false);
          
        } else {
          localStorage.removeItem(ACTIVE_ROLE_STORAGE_KEY);
          localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
          
          
          
          

          // CRITICAL: Set flags BEFORE return to prevent deadlock
          // These flags must be set so UI knows fetching is complete and can show selection screen
          setRoleFetchAttempted(true);
          setIsRoleLoading(false);
          setNeedsRoleSelection(true);

          // Return null to indicate role selection is needed
          return { role: null, orgId: null };
        }
      }

      
      
      // Fetch tenant_id from organizations table if org_id is available
      let tenantId: string | null = null;
      if (selectedOrgId) {
        try {
          const { data: orgData, error: orgError } = await supabase
            .from('organizations')
            .select('tenant_id')
            .eq('id', selectedOrgId)
            .single();

          if (!orgError && orgData) {
            // Type guard for Supabase response
            if (orgData && typeof orgData === 'object' && !('code' in orgData) && !('message' in orgData)) {
              const typedOrgData = orgData as { tenant_id: string | null | undefined };
              tenantId = typedOrgData.tenant_id || null;
              
            } else {
              
            }
          } else {
            
          }
        } catch (error) {
          console.error('[App] Błąd pobierania tenant_id:', error);
        }
      }
      
      // Update diagnostics on success
      setDiagnostics(prev => ({
        ...prev,
        roleStatus: selectedRole || 'NULL',
        orgIdStatus: selectedOrgId || 'NULL',
        tenantIdStatus: tenantId || 'NULL',
        errorStatus: null,
      }));
      
      // CRITICAL: Mark role fetch as attempted (success case)
      setRoleFetchAttempted(true);
      setIsRoleLoading(false);
      
      return { role: selectedRole, orgId: selectedOrgId };
    } catch (error) {
      const errorMessage = `Wyjątek podczas pobierania roli: ${error instanceof Error ? error.message : String(error)}`;
      console.error('[App] Błąd podczas pobierania roli:', error);
      setDiagnostics(prev => ({
        ...prev,
        errorStatus: errorMessage,
        roleStatus: 'ERROR',
        orgIdStatus: 'ERROR',
        tenantIdStatus: 'ERROR',
      }));
      
      // CRITICAL: Mark role fetch as attempted and stop loading (error case)
      setRoleFetchAttempted(true);
      setIsRoleLoading(false);
      
      return { role: null, orgId: null };
    }
  };

  // Function to check auth token in both localStorage and cookies
  // CRITICAL: Uses storageKey: 'domio-auth-token' - MUST match Supabase configuration exactly
  // This is non-negotiable for our SaaS architecture
  const checkAuthToken = () => {
    try {
      // CRITICAL: This key MUST be identical to storageKey in Supabase configuration
      const storageKey = 'domio-auth-token';
      
      // Check localStorage (for development)
      const localStorageValue = localStorage.getItem(storageKey);
      const hasAuthTokenInLocalStorage = localStorageValue !== null;
      
      // Check cookies (for production with SSO)
      // Searches for exact cookie name: 'domio-auth-token'
      const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith(`${storageKey}=`));
      const hasAuthTokenInCookie = cookieValue !== undefined;
      
      // Check if token exists in either location
      const hasAuthToken = hasAuthTokenInLocalStorage || hasAuthTokenInCookie;
      
      const authTokenStatus = hasAuthToken 
        ? `OK (${storageKey}${hasAuthTokenInCookie ? ' w ciastkach' : hasAuthTokenInLocalStorage ? ' w localStorage' : ''})` 
        : `BRAK (${storageKey} nie istnieje)`;
      
      const cookieStatus = hasAuthTokenInCookie
        ? `OK (ciastko ${storageKey} znalezione, domena: .domio.com.pl)`
        : `BRAK (ciastko ${storageKey} nie znalezione)`;
      
      
      
      setDiagnostics(prev => ({
        ...prev,
        authTokenStatus,
        cookieStatus,
      }));
      
      return hasAuthToken;
    } catch (error) {
      console.error('[DIAGNOSTICS] Błąd sprawdzania tokenu auth:', error);
      setDiagnostics(prev => ({
        ...prev,
        authTokenStatus: `ERROR: ${error instanceof Error ? error.message : String(error)}`,
        cookieStatus: 'ERROR',
      }));
      return false;
    }
  };

  useEffect(() => {
    let isMounted = true;
    let hasHandledInitialSession = false; // Flag to prevent double initialization
    let isInitialAppLoad = true; // Flag to track if this is the first app load

    // CRITICAL: Fail-safe timeout - force isLoading(false) after 3 seconds
    // This guarantees user never gets stuck with infinite spinner
    const failSafeTimeout = setTimeout(() => {
      if (isMounted) {
        console.warn('[App] Fail-safe timeout: Wymuszam ustawienie isLoading(false) po 3 sekundach');
        isLoadingRef.current = false;
        setIsLoading(false);
        setIsRoleLoading(false);
        setIsBridgeInitialized(true); // Also mark bridge as initialized to allow rendering
        // CRITICAL: Ustaw isInitialized na true w fail-safe timeout
        isInitializedRef.current = true;
        setIsInitialized(true);
      }
    }, 3000);

    // CRITICAL: Listen for bridge initialization completion event from main.tsx
    // This ensures we know when UniversalSessionBridge has finished its work
    // CRITICAL: Synchronizuj wszystkie stany w jednym batch update
    // Użyj funkcji aktualizującej, aby uniknąć "migania" interfejsu
    // CRITICAL: Po zakończeniu pracy przez UniversalSessionBridge, ustaw isInitialized na true
    // nawet jeśli sesja nie została znaleziona (to pozwoli na wyświetlenie Landing Page)
    // CRITICAL: Natychmiast ustaw setIsLoading(false) jeśli brak sesji - eliminuje fail-safe timeout
    const handleBridgeInitialized = async () => {
      
      
      // CRITICAL: Clear fail-safe timeout IMMEDIATELY to prevent 3-second delay
      clearTimeout(failSafeTimeout);
      
      // CRITICAL: Sprawdź sesję natychmiast po zakończeniu mostka
      // Jeśli brak sesji, natychmiast ustaw loading na false, aby nie czekać na timeout
      const { data: { session: bridgeSession } } = await supabase.auth.getSession();
      
      // CRITICAL: Synchronizuj wszystkie stany w jednym batch update
      // React automatycznie batchuje setState w event handlers, ale upewniamy się
      setIsBridgeInitialized(true);
      // CRITICAL: Ustaw isInitialized na true po zakończeniu mostka
      // To oznacza, że aplikacja może teraz renderować widoki (w tym Landing Page jeśli brak sesji)
      isInitializedRef.current = true;
      setIsInitialized(true);
      
      // CRITICAL: Jeśli brak sesji po zakończeniu mostka, natychmiast ustaw loading na false
      // To eliminuje potrzebę czekania na 3-sekundowy fail-safe timeout
      if (!bridgeSession) {
        
        isLoadingRef.current = false;
        setIsLoading(false);
        setIsRoleLoading(false);
      }
    };

      // Check if bridge has already initialized (in case event was fired before listener was set up)
      // Use a small delay to allow main.tsx to fire the event
      const checkBridgeStatus = () => {
        // Check if bridge initialization marker exists in sessionStorage
        const bridgeMarker = sessionStorage.getItem('domio-bridge-initialized');
        if (bridgeMarker === 'true') {
          
          // CRITICAL: Clear fail-safe timeout IMMEDIATELY to prevent 3-second delay
          clearTimeout(failSafeTimeout);
          // CRITICAL: Sprawdź sesję natychmiast
          supabase.auth.getSession().then(({ data: { session: bridgeSession } }) => {
            // CRITICAL: Synchronizuj wszystkie stany w jednym batch update
            setIsBridgeInitialized(true);
            // CRITICAL: Ustaw isInitialized na true po zakończeniu mostka
            isInitializedRef.current = true;
            setIsInitialized(true);
            
            // CRITICAL: Jeśli brak sesji, natychmiast ustaw loading na false
            if (!bridgeSession) {
              
              isLoadingRef.current = false;
              setIsLoading(false);
              setIsRoleLoading(false);
            }
          });
        } else {
          // Wait a bit more for the event
          setTimeout(() => {
            if (sessionStorage.getItem('domio-bridge-initialized') === 'true') {
              
              // CRITICAL: Clear fail-safe timeout IMMEDIATELY to prevent 3-second delay
              clearTimeout(failSafeTimeout);
              // CRITICAL: Sprawdź sesję natychmiast
              supabase.auth.getSession().then(({ data: { session: bridgeSession } }) => {
                setIsBridgeInitialized(true);
                isInitializedRef.current = true;
                setIsInitialized(true);
                
                // CRITICAL: Jeśli brak sesji, natychmiast ustaw loading na false
                if (!bridgeSession) {
                  
                  isLoadingRef.current = false;
                  setIsLoading(false);
                  setIsRoleLoading(false);
                }
              });
            }
          }, 200);
        }
      };

    // Listen for custom event from main.tsx
    window.addEventListener('domio-bridge-initialized', handleBridgeInitialized);
    checkBridgeStatus();

    // Check auth token immediately (domio-auth-token)
    checkAuthToken();

    // CRITICAL: Set up onAuthStateChange FIRST before checking session
    // This ensures we catch session changes from UniversalSessionBridge immediately
    // When bridge sets session via setSession(), this listener will fire and update state
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;
      
      
      
      // CRITICAL: Twarda blokada spinnera po inicjalizacji
      // ZASADA: Raz ustawione isInitialized = true musi na zawsze blokować możliwość wywołania setIsLoading(true)
      // Jeśli aplikacja jest już zainicjalizowana, tylko zaktualizuj sesję cicho, bez zmiany stanów ładowania
      // CRITICAL: Używaj ref.current zamiast state, aby uniknąć stale closure
      if (isInitializedRef.current && (event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED')) {
        
        setSession(session);
        return; // Natychmiastowy return - żadnych zmian stanów ładowania
      }
      
      // Handle SIGNED_IN - user logged in via Hub or UniversalSessionBridge
      // CRITICAL: Cichy Focus - wywołuj setLoading(true) tylko jeśli isInitialized === false
      // Jeśli użytkownik jest już zalogowany (session !== null), zdarzenie SIGNED_IN
      // (wywołane focusem) ma być obsługiwane "po cichu"
      // Dodatkowo, w onAuthStateChange, sprawdź czy zdarzenie to SIGNED_IN.
      // Jeśli tak, a isInitialized jest już true, to pod żadnym pozorem nie wywołuj setLoading(true).
      if (event === 'SIGNED_IN') {
        try {
          // CRITICAL: Clear fail-safe timeout IMMEDIATELY when session is received
          // This prevents 3-second delay
          clearTimeout(failSafeTimeout);
          
          
          
          // Pierwsze ładowanie - normalna obsługa (tylko gdy isInitialized === false)
          setSession(session);
          
          setDiagnostics(prev => ({
            ...prev,
            sessionStatus: session ? `OK (SIGNED_IN: ${session.user?.id?.substring(0, 8)}...)` : 'NULL',
            loadingStatus: true, // Tylko ustaw na true jeśli jeszcze nie zainicjalizowano
          }));
          
          // Step 2: Fetch user role and org_id
          if (session?.user?.id) {
            // CRITICAL: setLoading(true) tylko jeśli isInitialized === false
            // To gwarantuje, że przy focus events nie ustawimy loading state
            // CRITICAL: Używaj ref.current zamiast state, aby uniknąć stale closure
            if (!isInitializedRef.current) {
              isLoadingRef.current = true;
              setIsRoleLoading(true);
              setIsLoading(true);
            }
            try {
              const roleData = await fetchUserRole(session.user.id);
              if (!isMounted) return;
              
              setUserRole(roleData.role);
              setUserOrgId(roleData.orgId);
              
              setDiagnostics(prev => ({
                ...prev,
                loadingStatus: false,
                orgIdStatus: roleData.orgId || 'NULL',
              }));
            } catch (roleError) {
              console.error('[App] Błąd pobierania roli po SIGNED_IN:', roleError);
              setDiagnostics(prev => ({
                ...prev,
                errorStatus: roleError instanceof Error ? roleError.message : String(roleError),
                loadingStatus: false,
              }));
            } finally {
              if (isMounted) {
                setIsRoleLoading(false);
              }
            }
          } else {
            setUserRole(null);
            setUserOrgId(null);
            setDiagnostics(prev => ({
              ...prev,
              loadingStatus: false,
            }));
          }
        } catch (error) {
          console.error('[SSO DEBUG] Cleaning App: Błąd w obsłudze SIGNED_IN:', error);
          setDiagnostics(prev => ({
            ...prev,
            errorStatus: error instanceof Error ? error.message : String(error),
            loadingStatus: false,
          }));
        } finally {
          // CRITICAL: Always set loading to false, even if errors occurred
          // CRITICAL: Używaj ref.current zamiast state, aby uniknąć stale closure
          // Tylko jeśli aplikacja nie jest jeszcze zainicjalizowana
          if (isMounted && !isInitializedRef.current) {
            isLoadingRef.current = false;
            setIsLoading(false);
          }
        }
        return;
      }
      
      // Handle INITIAL_SESSION - initial check when app loads or after bridge sets session
      // This is critical for UniversalSessionBridge - when bridge sets session, this fires
      // CRITICAL: Cichy Focus - NIGDY nie ustawiaj setLoading(true) jeśli sesja już istnieje w stanie
      // Odświeżanie sesji przy powrocie do karty musi odbywać się całkowicie w tle
      // UWAGA: Blokada dla isInitialized === true jest już obsłużona wyżej
      if (event === 'INITIAL_SESSION') {
        try {
          // CRITICAL: Clear fail-safe timeout IMMEDIATELY when session is received
          // This prevents 3-second delay
          clearTimeout(failSafeTimeout);
          
          
          
          // CRITICAL: Cichy Focus - jeśli sesja już istnieje w stanie, tylko zaktualizuj cicho
          // NIGDY nie ustawiaj setLoading(true) dla INITIAL_SESSION gdy sesja już istnieje
          // Sprawdź aktualny stan sesji przed ustawieniem loading
          // Jeśli to nie pierwsze ładowanie i otrzymaliśmy sesję, tylko zaktualizuj cicho
          if (!isInitialAppLoad && session) {
            
            // Synchronizuj stan w jednym batch update
            setSession(session);
            // CRITICAL: Cichy focus - tylko jeśli aplikacja nie jest jeszcze zainicjalizowana
            if (!isInitializedRef.current) {
              isLoadingRef.current = false;
              setIsLoading(false);
              setIsRoleLoading(false);
            }
            return;
          }
          
          // CRITICAL: Prevent handling INITIAL_SESSION multiple times
          // If we've already handled it and this is a subsequent event, just update silently
          if (hasHandledInitialSession && !isInitialAppLoad) {
            
            // Just update session silently without blocking UI
            if (session) {
              setSession(session);
            }
            // CRITICAL: Cichy focus - tylko jeśli aplikacja nie jest jeszcze zainicjalizowana
            if (!isInitializedRef.current) {
              isLoadingRef.current = false;
              setIsLoading(false);
              setIsRoleLoading(false);
            }
            return;
          }
          
          // If this is NOT the first load AND session already exists with role, just update silently
          if (!isInitialAppLoad && session && userRole) {
            
            setSession(session);
            // CRITICAL: Cichy focus - tylko jeśli aplikacja nie jest jeszcze zainicjalizowana
            if (!isInitializedRef.current) {
              isLoadingRef.current = false;
              setIsLoading(false);
              setIsRoleLoading(false);
            }
            // Don't set loading states - just update session silently
            return;
          }
          
          hasHandledInitialSession = true; // Mark that we've handled initial session
          
          // CRITICAL: Synchronizuj wszystkie stany w jednym batch update
          // Użyj funkcji aktualizujących, aby uniknąć "migania" interfejsu
          setSession(session);
          
          // Step 2: Fetch user role and org_id if session exists
          // CRITICAL: setLoading(true) tylko jeśli isInitialized === false
          if (session?.user?.id) {
            // CRITICAL: setLoading(true) tylko jeśli isInitialized === false
            // To rozdziela pierwsze ładowanie od odświeżania w tle (focus events)
            // CRITICAL: Używaj ref.current zamiast state, aby uniknąć stale closure
            if (!isInitializedRef.current && isInitialAppLoad && isBridgeInitialized) {
              isLoadingRef.current = true;
              setIsRoleLoading(true);
              setIsLoading(true);
            }
            try {
              const roleData = await fetchUserRole(session.user.id);
              if (!isMounted) return;
              
              
              // CRITICAL: Synchronizuj wszystkie stany w jednym batch update
              setUserRole(roleData.role);
              setUserOrgId(roleData.orgId);
              isLoadingRef.current = false;
              setIsRoleLoading(false);
              setIsLoading(false);
              
              setDiagnostics(prev => ({
                ...prev,
                sessionStatus: session ? `OK (INITIAL: ${session.user?.id?.substring(0, 8)}...)` : 'NULL',
                loadingStatus: false,
                orgIdStatus: roleData.orgId || 'NULL',
              }));
            } catch (roleError) {
              console.error('[App] Błąd pobierania roli po INITIAL_SESSION:', roleError);
              setDiagnostics(prev => ({
                ...prev,
                errorStatus: roleError instanceof Error ? roleError.message : String(roleError),
                loadingStatus: false,
              }));
              // CRITICAL: Ustaw loading na false nawet przy błędzie
              isLoadingRef.current = false;
              setIsRoleLoading(false);
              setIsLoading(false);
            }
          } else {
            // CRITICAL: No session found - immediately set loading to false
            // This prevents deadlock when bridge doesn't find tokens
            // Synchronizuj wszystkie stany w jednym batch update
            setUserRole(null);
            setUserOrgId(null);
            setSession(null);
            isLoadingRef.current = false;
            setIsRoleLoading(false);
            setIsLoading(false);
            
            setDiagnostics(prev => ({
              ...prev,
              sessionStatus: 'NULL',
              loadingStatus: false,
            }));
          }
        } catch (error) {
          console.error('[SSO DEBUG] Cleaning App: Błąd w obsłudze INITIAL_SESSION:', error);
          setDiagnostics(prev => ({
            ...prev,
            errorStatus: error instanceof Error ? error.message : String(error),
            loadingStatus: false,
          }));
          // CRITICAL: Ustaw loading na false nawet przy błędzie
          isLoadingRef.current = false;
          setIsLoading(false);
          setIsRoleLoading(false);
        } finally {
          // CRITICAL: Always set loading to false, even if errors occurred
          // BUT: Only if bridge is initialized to prevent flicker
          if (isMounted && isBridgeInitialized) {
            isLoadingRef.current = false;
            setIsLoading(false);
            setIsRoleLoading(false);
            // Mark that initial load is complete
            isInitialAppLoad = false;
            // CRITICAL: Ustaw isInitialized na true po zakończeniu pierwszej inicjalizacji
            // To pozwoli na wyświetlenie widoków (w tym Landing Page jeśli brak sesji)
            if (!isInitializedRef.current) {
              isInitializedRef.current = true;
              setIsInitialized(true);
            }
          }
        }
        return;
      }
      
      // Handle TOKEN_REFRESHED - session still valid, cookie refreshed
      // CRITICAL: Cichy Focus - Token refresh happens in background (e.g., when returning to tab)
      // ZABRANIA SIĘ wywoływania setLoading(true) dla TOKEN_REFRESHED
      // Odświeżanie sesji przy powrocie do karty przeglądarki musi odbywać się całkowicie w tle
      // Jeśli użytkownik jest już zalogowany (session !== null), zdarzenie TOKEN_REFRESHED
      // ma być obsługiwane "po cichu" - aktualizuj tylko dane sesji, ale nie zmieniaj stanu ładowania
      // UWAGA: Blokada dla isInitialized === true jest już obsłużona wyżej
      if (event === 'TOKEN_REFRESHED') {
        try {
          
          
          // CRITICAL: Synchronizuj wszystkie stany w jednym batch update
          // NIGDY nie ustawiaj setLoading(true) dla TOKEN_REFRESHED
          setSession(session);
          // CRITICAL: Cichy focus - tylko jeśli aplikacja nie jest jeszcze zainicjalizowana
          if (!isInitializedRef.current) {
            isLoadingRef.current = false;
            setIsLoading(false);
            setIsRoleLoading(false);
          }
          
          // Role should remain the same - don't refetch to avoid blocking UI
          // Only update if session user ID changed (shouldn't happen, but safety check)
          if (session?.user?.id && session.user.id !== session?.user?.id) {
            // User ID changed (unlikely), silently refresh role in background
            fetchUserRole(session.user.id).then(roleData => {
              if (isMounted) {
                setUserRole(roleData.role);
                setUserOrgId(roleData.orgId);
              }
            }).catch(error => {
              console.error('[SSO DEBUG] Cleaning App: Błąd pobierania roli po TOKEN_REFRESHED:', error);
            });
          }
        } catch (error) {
          console.error('[SSO DEBUG] Cleaning App: Błąd w obsłudze TOKEN_REFRESHED:', error);
          // CRITICAL: Ustaw loading na false nawet przy błędzie
          // CRITICAL: Tylko jeśli aplikacja nie jest jeszcze zainicjalizowana
          if (!isInitializedRef.current) {
            isLoadingRef.current = false;
            setIsLoading(false);
            setIsRoleLoading(false);
          }
        } finally {
          // CRITICAL: Ensure loading is false (token refresh should NEVER block UI)
          // CRITICAL: Tylko jeśli aplikacja nie jest jeszcze zainicjalizowana
          if (isMounted && !isInitializedRef.current) {
            isLoadingRef.current = false;
            setIsLoading(false);
            setIsRoleLoading(false);
          }
        }
        return;
      }
      
      // Handle SIGNED_OUT - session cookie removed
      // Wymuś przekierowanie do logowania (zamiast pustego ekranu/błędów)
      if (event === 'SIGNED_OUT') {
        
        localStorage.removeItem(ACTIVE_ROLE_STORAGE_KEY);
        localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
        setSession(null);
        setUserRole(null);
        setUserOrgId(null);
        isLoadingRef.current = false;
        setIsRoleLoading(false);
        setIsLoading(false);
        redirectToLogin();
        return;
      }
      
      // Update session state for any other event
      setSession(session);
      if (event === 'USER_UPDATED' || event === 'PASSWORD_RECOVERY') {
        try {
          // Refresh role and org_id if session exists
          if (session?.user?.id) {
            setIsRoleLoading(true);
            try {
              const roleData = await fetchUserRole(session.user.id);
              if (!isMounted) return;
              setUserRole(roleData.role);
              setUserOrgId(roleData.orgId);
            } catch (roleError) {
              console.error('[App] Błąd pobierania roli po USER_UPDATED/PASSWORD_RECOVERY:', roleError);
            } finally {
              if (isMounted) {
                setIsRoleLoading(false);
              }
            }
          }
        } catch (error) {
          console.error('[SSO DEBUG] Cleaning App: Błąd w obsłudze USER_UPDATED/PASSWORD_RECOVERY:', error);
        } finally {
          // CRITICAL: Always set loading to false
          // CRITICAL: Tylko jeśli aplikacja nie jest jeszcze zainicjalizowana
          if (isMounted && !isInitializedRef.current) {
            isLoadingRef.current = false;
            setIsLoading(false);
          }
        }
      } else {
        // For any other event, ensure loading is false
        // CRITICAL: Tylko jeśli aplikacja nie jest jeszcze zainicjalizowana
        if (isMounted && !isInitializedRef.current) {
          isLoadingRef.current = false;
          setIsLoading(false);
          setIsRoleLoading(false);
        }
      }
    });

    // CRITICAL: 2-step initialization - session first, then role
    // Step 1: Check session immediately on app load
    // This reads session from cookie (storageKey: 'domio-auth-token')
    // NOTE: UniversalSessionBridge in main.tsx should have already set the session,
    // but we check here as a fallback and to ensure state is synchronized
    const checkInitialSession = async () => {
      try {
        // CRITICAL: Wait for bridge initialization to complete before checking session
        // This prevents race conditions and ensures bridge has finished its work
        // Use sessionStorage directly (not React state) to avoid stale closure issues
        let bridgeCheckAttempts = 0;
        const maxBridgeCheckAttempts = 50; // 5 seconds max wait (50 * 100ms)
        
        while (sessionStorage.getItem('domio-bridge-initialized') !== 'true' && bridgeCheckAttempts < maxBridgeCheckAttempts) {
          await new Promise(resolve => setTimeout(resolve, 100));
          bridgeCheckAttempts++;
        }
        
        // Update React state after checking sessionStorage
        const bridgeInitialized = sessionStorage.getItem('domio-bridge-initialized') === 'true';
        if (bridgeInitialized) {
          setIsBridgeInitialized(true);
        }
        
        if (!isMounted) return;
        
        // CRITICAL: Wait a bit for onAuthStateChange to handle INITIAL_SESSION first
        // This prevents race conditions between checkInitialSession and onAuthStateChange
        await new Promise(resolve => setTimeout(resolve, 100));
        
        if (!isMounted) return;
        
        // If INITIAL_SESSION was already handled by onAuthStateChange, skip fetching role here
        // The onAuthStateChange handler will have already fetched the role
        if (hasHandledInitialSession) {
          
          // CRITICAL: Always ensure loading is false when INITIAL_SESSION was handled
          // This prevents deadlock if session is null
          isLoadingRef.current = false;
          setIsLoading(false);
          setIsRoleLoading(false);
          // CRITICAL: Ustaw isInitialized jeśli mostek jest gotowy i INITIAL_SESSION został obsłużony
          if (bridgeInitialized && !isInitializedRef.current) {
            isInitializedRef.current = true;
            setIsInitialized(true);
          }
          return;
        }
        
        // CRITICAL: Jeśli mostek zakończył pracę i nie ma sesji, natychmiast zakończ ładowanie
        // To eliminuje potrzebę czekania na 3-sekundowy fail-safe timeout
        if (bridgeInitialized) {
          const { data: { session: quickCheckSession } } = await supabase.auth.getSession();
          if (!quickCheckSession) {
            
            setSession(null);
            setUserRole(null);
            setUserOrgId(null);
            isLoadingRef.current = false;
            setIsLoading(false);
            setIsRoleLoading(false);
            if (!isInitializedRef.current) {
              isInitializedRef.current = true;
              setIsInitialized(true);
            }
            setDiagnostics(prev => ({
              ...prev,
              sessionStatus: 'NULL',
              loadingStatus: false,
            }));
            return;
          }
        }
        
        

        // Update diagnostics - checking session
        setDiagnostics(prev => ({
          ...prev,
          sessionStatus: 'Checking...',
          loadingStatus: true,
          tenantIdStatus: 'Loading...',
        }));
        
        // Extract specific auth cookie for debugging
        const authCookie = document.cookie
          .split('; ')
          .find(row => row.startsWith('domio-auth-token'))
        
        
        // Try to get session from cookies (first attempt)
        
        const { data: { session }, error: getSessionError } = await supabase.auth.getSession();
        
        if (getSessionError) {
          
        }
        
        if (!isMounted) return;
        
        
        
        // Update diagnostics with session status
        if (session) {
          setDiagnostics(prev => ({
            ...prev,
            sessionStatus: `OK (user: ${session.user?.id?.substring(0, 8)}...)`,
          }));
        } else {
          setDiagnostics(prev => ({
            ...prev,
            sessionStatus: 'NULL',
          }));
        }
        
        // If session is empty, try multiple strategies to read from cookie
        let finalSession = session;
        
        if (!session) {
          
          
          // Strategy 1: refreshSession (forces reading from cookie)
          const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
          
          if (refreshError) {
            
          }
          
          if (refreshedSession) {
            
            finalSession = refreshedSession;
            setDiagnostics(prev => ({
              ...prev,
              sessionStatus: `OK (refreshed: ${refreshedSession.user?.id?.substring(0, 8)}...)`,
            }));
          } else {
            // Strategy 2: Wait a bit and try again (cookie might not be immediately available)
            
            await new Promise(resolve => setTimeout(resolve, 500));
            
            if (!isMounted) return;
            
            const { data: { session: retrySession } } = await supabase.auth.getSession();
            if (retrySession) {
              
              finalSession = retrySession;
              setDiagnostics(prev => ({
                ...prev,
                sessionStatus: `OK (retry: ${retrySession.user?.id?.substring(0, 8)}...)`,
              }));
            } else {
              
              setDiagnostics(prev => ({
                ...prev,
                sessionStatus: 'NULL (wszystkie próby wyczerpane)',
              }));
            }
          }
        } else {
          
        }

        if (!isMounted) return;
        
        // Step 2: If session exists, fetch user role and org_id
        // Only do this if INITIAL_SESSION wasn't already handled
        if (finalSession?.user?.id && !hasHandledInitialSession) {
          setSession(finalSession);
          if (!isInitializedRef.current) {
            isLoadingRef.current = true;
            setIsRoleLoading(true);
          }
          
          setDiagnostics(prev => ({
            ...prev,
            loadingStatus: true,
            roleStatus: 'Loading...',
            orgIdStatus: 'Loading...',
          }));
          
          
          const roleData = await fetchUserRole(finalSession.user.id);
          
          if (!isMounted) return;
          
          
          setUserRole(roleData.role);
          setUserOrgId(roleData.orgId);
          // Only set loading to false if bridge is initialized
          if (sessionStorage.getItem('domio-bridge-initialized') === 'true') {
            isLoadingRef.current = false;
            setIsRoleLoading(false);
            setIsLoading(false);
            // CRITICAL: Ustaw isInitialized na true po zakończeniu inicjalizacji
            if (!isInitializedRef.current) {
              isInitializedRef.current = true;
              setIsInitialized(true);
            }
          }
          
          setDiagnostics(prev => ({
            ...prev,
            loadingStatus: false,
            orgIdStatus: roleData.orgId || 'NULL',
          }));
        } else {
          // CRITICAL: No session found - user is not logged in
          // Immediately set loading to false to prevent deadlock
          if (!finalSession) {
            setSession(null);
            setUserRole(null);
            setUserOrgId(null);
          }
          // Always set loading to false when no session is found
          // This prevents deadlock when bridge doesn't find tokens
          isLoadingRef.current = false;
          setIsRoleLoading(false);
          setIsLoading(false);
          
          setDiagnostics(prev => ({
            ...prev,
            sessionStatus: finalSession ? prev.sessionStatus : 'NULL',
            roleStatus: finalSession ? prev.roleStatus : 'N/A',
            orgIdStatus: finalSession ? prev.orgIdStatus : 'N/A',
            loadingStatus: false,
          }));
        }
      } catch (error) {
        console.error('[SSO DEBUG] Cleaning App: Błąd podczas sprawdzania sesji:', error);
        if (isMounted) {
          setSession(null);
          setUserRole(null);
          setUserOrgId(null);
          // CRITICAL: Always set loading to false on error to prevent deadlock
          isLoadingRef.current = false;
          setIsRoleLoading(false);
          setIsLoading(false);
          
          setDiagnostics(prev => ({
            ...prev,
            sessionStatus: 'ERROR',
            errorStatus: error instanceof Error ? error.message : String(error),
            loadingStatus: false,
          }));
        }
      } finally {
        // CRITICAL: Always set loading to false, regardless of success or error
        // This ensures the app never gets stuck in infinite loading state
        // If bridge is initialized OR if no session was found, set loading to false
        if (isMounted) {
          const bridgeInitialized = sessionStorage.getItem('domio-bridge-initialized') === 'true';
          
          // CRITICAL: Sprawdź ponownie sesję w finally - jeśli mostek zakończył pracę i brak sesji,
          // natychmiast zakończ ładowanie (eliminuje fail-safe timeout)
          if (bridgeInitialized) {
            const { data: { session: finalCheckSession } } = await supabase.auth.getSession();
            if (!finalCheckSession) {
              
              setSession(null);
              setUserRole(null);
              setUserOrgId(null);
              isLoadingRef.current = false;
              setIsLoading(false);
              setIsRoleLoading(false);
              if (!isInitializedRef.current) {
                isInitializedRef.current = true;
                setIsInitialized(true);
              }
              setDiagnostics(prev => ({
                ...prev,
                sessionStatus: 'NULL',
                loadingStatus: false,
              }));
              isInitialAppLoad = false;
              return;
            }
          }
          
          // Set loading to false if:
          // 1. Bridge is initialized, OR
          // 2. No session was found (prevents deadlock when bridge doesn't find tokens)
          if (bridgeInitialized) {
            isLoadingRef.current = false;
            setIsLoading(false);
            setIsRoleLoading(false);
            
            setDiagnostics(prev => ({
              ...prev,
              loadingStatus: false,
            }));
            
            // CRITICAL: Ustaw isInitialized na true po zakończeniu inicjalizacji
            // To pozwoli na wyświetlenie widoków (w tym Landing Page jeśli brak sesji)
            if (!isInitializedRef.current) {
              isInitializedRef.current = true;
              setIsInitialized(true);
            }
          }
          
          // Mark that initial load is complete
          isInitialAppLoad = false;
        }
      }
    };

    // Check session immediately (as fallback - UniversalSessionBridge should have already set it)
    checkInitialSession();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      window.removeEventListener('domio-bridge-initialized', handleBridgeInitialized);
      clearTimeout(failSafeTimeout);
    };
  }, []);

  // Handle role selection from RoleSelectionPage
  // CRITICAL: This function must be defined before any early returns
  const handleRoleSelect = (role: string, orgId: string | null) => {
    
    localStorage.setItem(ACTIVE_ROLE_STORAGE_KEY, role);
    if (orgId) {
      localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, orgId);
    } else {
      localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
    }
    setUserRole(role as UserRole);
    setUserOrgId(orgId);
    setNeedsRoleSelection(false);
    // State update will trigger re-render and show appropriate dashboard
    // Automatic redirect will be handled by useEffect below
  };

  // Ref to track if redirect has already been performed (prevents infinite loops)
  const hasRedirectedRef = useRef(false);

  /** Cleaner SPA routes: list + property + section deep-links must not trigger hard redirect to /. */
  const isCleanerAllowedPath = (pathname: string): boolean => {
    if (pathname === '/') return true;
    if (/^\/property\/[^/]+\/section\/[^/]+$/.test(pathname)) return true;
    if (/^\/property\/[^/]+$/.test(pathname)) return true;
    return false;
  };

  const isAdminAllowedPath = (pathname: string): boolean => {
    if (pathname === '/') return true;
    if (pathname === '/dashboard') return true;
    if (pathname.startsWith('/properties/')) return true;
    if (pathname.startsWith('/workers/')) return true;
    if (pathname === '/finance') return true;
    if (pathname === '/equipment/protocols') return true;
    return false;
  };

  // Hard route guard: enforce currently active role from localStorage.
  // Prevents role regression when user has multiple memberships.
  useEffect(() => {
    if (!session || !userRole || needsRoleSelection) {
      // Reset redirect flag when conditions change
      hasRedirectedRef.current = false;
      return;
    }
    
    // Prevent infinite redirect loops
    if (hasRedirectedRef.current) {
      return;
    }
    
    const persistedRoleRaw = localStorage.getItem(ACTIVE_ROLE_STORAGE_KEY);
    const persistedRole =
      persistedRoleRaw === 'owner' || persistedRoleRaw === 'coordinator' || persistedRoleRaw === 'cleaner'
        ? (persistedRoleRaw as UserRole)
        : null;

    // Keep runtime state aligned with explicit active role.
    if (persistedRole && persistedRole !== userRole) {
      setUserRole(persistedRole);
      return;
    }

    // Only redirect if we're not already on the correct route
    const currentPath = window.location.pathname;
    const normalizedRole = (persistedRole ?? userRole).toLowerCase();
    
    // For cleaner: only hard-redirect unknown paths to /. Deep-links (/property/..., /property/.../section/...) stay in SPA.
    if (normalizedRole === 'cleaner' && !isCleanerAllowedPath(currentPath)) {
      
      hasRedirectedRef.current = true; // Mark as redirected to prevent loops
      window.location.href = '/';
      return;
    }

    // For admin roles: block cleaner paths and any unknown non-admin paths.
    if ((normalizedRole === 'owner' || normalizedRole === 'coordinator') && !isAdminAllowedPath(currentPath)) {
      
      hasRedirectedRef.current = true;
      window.location.href = '/dashboard';
    }
  }, [session, userRole, needsRoleSelection]);

  // CRITICAL: Check is_first_login flag and redirect to Hub change-password if needed
  // This check must happen AFTER session is loaded but BEFORE rendering main app
  useEffect(() => {
    const checkFirstLogin = async () => {
      // Only check if session exists and app is initialized
      if (!session?.user?.id || !isInitialized || isLoading || isRoleLoading) {
        return;
      }

      try {
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('is_first_login, account_type, fleet_role')
          .eq('id', session.user.id)
          .single();

        if (profileError) {
          console.error('[App] Błąd pobierania profilu dla is_first_login:', profileError);
          return;
        }

        if (shouldForcePasswordChange(profile)) {
          redirectToHubChangePassword();
        }
      } catch (error) {
        console.error('[App] Błąd podczas sprawdzania is_first_login:', error);
      }
    };

    checkFirstLogin();
  }, [session, isInitialized, isLoading, isRoleLoading]);

  useEffect(() => {
    if (!session) {
      setModuleAccess("pending");
      return;
    }
    let cancelled = false;
    setModuleAccess("pending");
    void userHasModuleAccess(supabase, "cleaning").then((allowed) => {
      if (!cancelled) setModuleAccess(allowed ? "allowed" : "denied");
    });
    return () => {
      cancelled = true;
    };
  }, [session]);

  // CRITICAL: BRAMKA INICJALIZACJI
  // Dopóki flaga isInitialized jest na false, aplikacja nie ma prawa wyrenderować
  // niczego poza spinnerem (żadnych tras chronionych, żadnych komunikatów o braku sesji)
  // To jest "Bramka Inicjalizacji" - pierwsza i najważniejsza kontrola dostępu
  // Komunikat "Brak autoryzacji" lub Landing Page może się pojawić tylko wtedy,
  // gdy isInitialized === true ORAZ session === null
  if (!isInitialized) {
    return (
      <LoadingSpinner />
    );
  }

  // CRITICAL: 2-step initialization - wait for session AND role
  // Block rendering until:
  // 1. Session state is determined (isLoading === false) - WAIT for first getSession() check
  // 2. User role is fetched (isRoleLoading === false) - tylko jeśli sesja istnieje
  // EXCEPTION: If no session exists, don't wait for role loading
  // IMPORTANT: During initial session verification, show spinner instead of redirecting to /login
  // FIX: Dodaj sprawdzanie czy getSession() jeszcze trwa - nie przekierowuj zanim nie zakończy działania
  const shouldShowSpinner =
    isLoading ||
    (isRoleLoading && session !== null) ||
    (session !== null && moduleAccess === "pending");
  
  if (shouldShowSpinner) {
    return (
      <LoadingSpinner />
    );
  }

  if (session && moduleAccess === "denied") {
    return (
        <ThemeProvider
          attribute="class"
          defaultTheme="system"
          enableSystem
          disableTransitionOnChange
        >
          <ThemeSync />
          <LanguageSync />
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <NoModuleAccess />
          </TooltipProvider>
        </ThemeProvider>
    );
  }

  // CRITICAL: Show role selection page if user has multiple roles and hasn't selected one
  // PRIORYTET: Ten blok MUSI być sprawdzany PRZED blokiem błędu autoryzacji
  // Warunek niezależny od userRole (która musi być null przy wyborze)
  // Wymagane: needsRoleSelection=true, roleFetchAttempted=true, dostępne memberships
  if (session && needsRoleSelection && roleFetchAttempted && availableMemberships.length > 0 && isInitialized && !isLoading && !isRoleLoading) {
    return (
        <ThemeProvider
          attribute="class"
          defaultTheme="system"
          enableSystem
          disableTransitionOnChange
        >
          <ThemeSync />
          <LanguageSync />
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <RoleSelectionPage
              memberships={availableMemberships}
              onRoleSelect={handleRoleSelect}
            />
          </TooltipProvider>
        </ThemeProvider>
    );
  }

  // CRITICAL CHANGE: If session exists, NEVER show Landing Page
  // If session exists but no role, show error message instead
  // IMPORTANT: Only show error when ALL conditions are met:
  // - Application is initialized (isInitialized === true) - już sprawdzone wyżej przez Bramkę
  // - Loading is complete (isLoading === false && isRoleLoading === false) - już sprawdzone wyżej
  // - Role fetch has been attempted (roleFetchAttempted === true) - to gwarantuje, że ramka pojawi się TYLKO gdy system skończył szukać roli
  // - needsRoleSelection === false - NIE pokazuj błędu jeśli system czeka na wybór użytkownika
  // NAKAZ: Ten komponent musi mieć ZAKAZ zwracania czegokolwiek innego niż null lub <Loading />,
  // dopóki isInitialized nie jest true ORAZ loading nie jest zakończony ORAZ roleFetchAttempted nie jest true
  // Jeśli widzę ramkę, to znaczy, że ten komponent renderuje się ZA WCZEŚNIE
  // USZCZELNIENIE: Błąd autoryzacji pojawia się TYLKO gdy wybór roli NIE jest wymagany
  if (session && !userRole && !needsRoleSelection && isInitialized && !isLoading && !isRoleLoading && roleFetchAttempted) {
    return (
        <ThemeProvider
          attribute="class"
          defaultTheme="system"
          enableSystem
          disableTransitionOnChange
        >
          <ThemeSync />
          <LanguageSync />
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <div className="flex items-center justify-center min-h-screen p-4">
              <div className="max-w-md w-full bg-card border border-destructive rounded-lg p-6 space-y-4">
                <h1 className="text-2xl font-bold text-destructive">Błąd autoryzacji</h1>
                <p className="text-muted-foreground">
                  Błąd: Brak przypisanej roli w systemie DOMIO dla Twojego konta.
                </p>
                <p className="text-sm text-muted-foreground">
                  Session: {session ? 'Aktywna' : 'Brak'} | 
                  User ID: {session?.user?.id?.substring(0, 8)}... | 
                  Role: {userRole || 'NULL'}
                </p>
              </div>
            </div>
          </TooltipProvider>
        </ThemeProvider>
    );
  }

  // CRITICAL: Render landing page ONLY when ALL conditions are met:
  // 1. Application is initialized (isInitialized === true) - już sprawdzone wyżej (Bramka Inicjalizacji)
  // 2. Loading is complete (isLoading === false && isRoleLoading === false) - już sprawdzone wyżej
  // 3. Session is null (user is definitely not logged in)
  // Komunikat "Brak autoryzacji" lub Landing Page może pojawić się tylko i wyłącznie,
  // gdy: isInitialized === true ORAZ isLoading === false ORAZ session === null
  // NAKAZ: Dopóki isInitialized nie jest true, zwróć null lub <Loading />
  if (!session && isInitialized && !isLoading && !isRoleLoading) {
    return (
        <ThemeProvider
          attribute="class"
          defaultTheme="system"
          enableSystem
          disableTransitionOnChange
        >
          <ThemeSync />
          <LanguageSync />
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <LandingPage />
          </TooltipProvider>
        </ThemeProvider>
    );
  }

  // Session exists - render based on user role
  // IMPORTANT: RoleProvider must wrap components that use useRole()
  // Gwarancja blokady: Nie renderuj dashboardów dopóki rola nie jest wybrana
  // Jeśli needsRoleSelection === true, ekran wyboru został już wyświetlony wyżej

  // If role is 'cleaner', render CleanerDashboard directly (main view for cleaners)
  // Tylko jeśli rola jest wybrana (userRole !== null) i nie wymaga wyboru (needsRoleSelection === false)
  if (userRole === 'cleaner' && !needsRoleSelection) {
    return (
        <ThemeProvider
          attribute="class"
          defaultTheme="system"
          enableSystem
          disableTransitionOnChange
        >
          <ThemeSync />
          <LanguageSync />
          <TooltipProvider>
            <RoleProvider initialRole={userRole} initialOrgId={userOrgId}>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <CleanerRuntimeGuards />
                <Routes>
                  <Route path="/" element={<CleanerDashboard />} />
                  <Route path="/property/:propertyId" element={<CleanerPropertyDetailsPage />} />
                  <Route path="/property/:propertyId/section/:sectionId" element={<SectionDetailsPage />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </BrowserRouter>
            </RoleProvider>
          </TooltipProvider>
        </ThemeProvider>
    );
  }

  // For other roles (owner, coordinator) or if role is null, render Index with routing
  // Index will handle role-specific rendering internally
  // Gwarancja: Renderuj tylko jeśli rola jest wybrana (userRole !== null) i nie wymaga wyboru
  if (!userRole || needsRoleSelection) {
    // Should not reach here - needsRoleSelection should be handled above
    // But as a safety measure, show loading spinner
    console.warn('[App] Próba renderowania bez wybranej roli - wyświetlanie spinnera');
    return (
        <ThemeProvider
          attribute="class"
          defaultTheme="system"
          enableSystem
          disableTransitionOnChange
        >
          <ThemeSync />
          <LanguageSync />
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <LoadingSpinner />
          </TooltipProvider>
        </ThemeProvider>
    );
  }

  // Check if user role is owner or coordinator (admin roles)
  const isAdminRole = userRole === 'owner' || userRole === 'coordinator';

  return (
      <ThemeProvider
        attribute="class"
        defaultTheme="system"
        enableSystem
        disableTransitionOnChange
      >
        <ThemeSync />
        <LanguageSync />
        <TooltipProvider>
          <RoleProvider initialRole={userRole} initialOrgId={userOrgId}>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              {isAdminRole ? (
                <Routes>
                  <Route element={<AdminLayout />}>
                    {/* Automatic redirect from / to /dashboard for owner/coordinator */}
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<AdminDashboard />} />
                    <Route path="/equipment/protocols" element={<EquipmentProtocolHistoryPage />} />
                    {/* Same deep-links as cleaner app — F5 / shared URL must not hit 404 for owner/coordinator */}
                    <Route
                      path="/property/:propertyId/section/:sectionId"
                      element={<SectionDetailsPage />}
                    />
                    <Route
                      path="/property/:propertyId"
                      element={<CleanerPropertyDetailsPage />}
                    />
                    <Route path="/properties/:id" element={<AdminPropertyDetailsPage />} />
                    <Route path="/workers/:id" element={<WorkerDetailsPage />} />
                    <Route path="/finance" element={<Navigate to="/dashboard?tab=finance" replace />} />
                    {/* ADD ALL CUSTOM ADMIN ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                    <Route path="*" element={<NotFound />} />
                  </Route>
                </Routes>
              ) : (
                <Routes>
                  <Route path="/" element={<Index />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              )}
            </BrowserRouter>
          </RoleProvider>
        </TooltipProvider>
      </ThemeProvider>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AppInner />
  </QueryClientProvider>
);

export default App;
